<script setup>
import { message } from 'ant-design-vue'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'

const asset = (name) =>
  `${import.meta.env.BASE_URL}image/memcore-redesign/${name}`
const showComingSoon = () => message.info('MemCore 工作台暂未开放，敬请期待。')
const capabilities = [
  {
    icon: 'memory.svg',
    title: '长期记忆',
    description:
      '将对话、项目和知识持续沉淀，跨会话保留上下文，避免重要信息在重复沟通中流失。',
  },
  {
    icon: 'grid.svg',
    title: '结构化整理',
    description:
      '自动从对话中提取主题、决策、情绪与行动项，让碎片化信息成为可检索的知识资产。',
  },
  {
    icon: 'graph.svg',
    title: '关联与治理',
    description:
      '通过记忆图谱发现人、事、地点和项目之间的关系，并为团队协作提供清晰的上下文边界。',
  },
]
const steps = [
  '记录对话、文档与项目动态',
  '提取主题、决策和关键线索',
  '形成可浏览的记忆图谱',
  '在新任务中精准召回上下文',
]
</script>

<template>
  <Header design-always hide-banner />
  <main class="memcore-page">
    <section class="memcore-hero" aria-labelledby="memcore-title">
      <div class="memcore-container hero-layout">
        <div class="hero-copy">
          <h1 id="memcore-title">MemCore记忆引擎</h1>
          <p class="hero-description">
            MemCore
            将零散对话、项目决策和协作经验沉淀为可追溯、可关联、可复用的长期记忆，让每个智能体都拥有真正理解你的上下文。
          </p>
          <button
            class="experience-button"
            v-track="'memcore > experience'"
            type="button"
            @click="showComingSoon"
          >
            敬请期待
          </button>
        </div>
        <div class="hero-art">
          <img
            :src="asset('hero.webp')"
            alt="MemCore 长期记忆引擎主题示意"
            fetchpriority="high"
          />
        </div>
      </div>
    </section>

    <section
      class="memcore-section capabilities-section"
      aria-label="MemCore 核心能力"
    >
      <div class="memcore-container capability-grid">
        <article
          v-for="item in capabilities"
          :key="item.title"
          class="capability-card"
        >
          <div class="capability-heading">
            <span class="capability-icon"
              ><img
                :src="asset(item.icon)"
                alt=""
                width="24"
                height="24"
                loading="lazy"
            /></span>
            <h3>{{ item.title }}</h3>
          </div>
          <p>{{ item.description }}</p>
        </article>
      </div>
    </section>

    <section
      class="memcore-section workflow-section"
      aria-labelledby="workflow-title"
    >
      <div class="memcore-container">
        <div class="section-heading">
          <h2 id="workflow-title">从对话片段，到可复用的团队认知</h2>
          <p>
            记忆在输入后被抽取、归类与关联，关键结论始终可追溯，后续任务也能获得准确的上下文支持。
          </p>
        </div>
        <div class="workflow-layout">
          <img
            class="workflow-image"
            :src="asset('workflow.webp')"
            alt="MemCore 对话记录、记忆整理与关联界面示意"
            loading="lazy"
          />
          <ol class="workflow-list">
            <li v-for="(step, index) in steps" :key="step">
              <span class="step-number">{{
                String(index + 1).padStart(2, '0')
              }}</span>
              <span>{{ step }}</span>
            </li>
          </ol>
        </div>
      </div>
    </section>
  </main>
  <DesignFooter />
</template>

<style scoped>
.memcore-page {
  width: 100%;
  min-width: 0;
  color: #14233b;
  line-height: 1.5;
}
.memcore-page,
.memcore-page *,
.memcore-page *::before,
.memcore-page *::after {
  box-sizing: border-box;
}
.memcore-page h1,
.memcore-page h2,
.memcore-page h3,
.memcore-page p,
.memcore-page ol {
  margin: 0;
}
.memcore-page img {
  display: block;
  max-width: 100%;
}
.memcore-container {
  width: min(1440px, calc(100% - 48px));
  margin: 0 auto;
}
.memcore-hero {
  display: flex;
  align-items: center;
  min-height: 416px;
  padding: 48px 0;
  background: #f4f5f7;
}
.hero-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  align-items: center;
}
.hero-copy {
  min-width: 0;
}
.hero-copy h1 {
  margin-top: 4px;
  font-size: 30px;
  font-weight: 600;
  line-height: 1.3;
}
.hero-description {
  margin-top: 16px !important;
  color: #5c6c81;
  font-size: 16px;
  line-height: 1.75;
}
.experience-button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  min-height: 48px;
  margin-top: 32px;
  padding: 12px 24px;
  border: 0;
  border-radius: 4px;
  background: #ed000c;
  color: #fff;
  font: inherit;
  font-size: 16px;
  cursor: pointer;
}
.experience-button:hover {
  background: #cb000a;
}
.experience-button:focus-visible {
  outline: 2px solid #ed000c;
  outline-offset: 4px;
}
.hero-art {
  position: relative;
  min-width: 0;
}
.hero-art img {
  width: 100%;
  height: auto;
  object-fit: contain;
}
.hero-art::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, #f4f5f7, transparent 7%, transparent 93%, #f4f5f7),
    linear-gradient(180deg, #f4f5f7, transparent 8%, transparent 92%, #f4f5f7);
}
.memcore-section {
  padding: 64px 0;
}
.capability-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 24px;
  align-items: stretch;
}
.capability-card {
  min-width: 0;
  padding: 24px;
  border-radius: 8px;
  background: #f4f7fb;
}
.capability-heading {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.capability-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  flex: 0 0 40px;
  border-radius: 50%;
  background: #fff;
}
.capability-card h3 {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}
.capability-card p {
  color: #617089;
  font-size: 16px;
  line-height: 28px;
}
.workflow-section {
  padding-top: 48px;
  background: #f4f7fb;
}
.section-heading {
  margin-bottom: 40px;
  text-align: center;
}
.section-heading h2 {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.4;
}
.section-heading p {
  margin-top: 16px;
  color: #617089;
  font-size: 16px;
  line-height: 24px;
}
.workflow-layout {
  display: grid;
  grid-template-columns: 596fr 804fr;
  align-items: center;
  gap: 32px;
}
.workflow-image {
  width: 100%;
  height: auto;
  max-height: 320px;
  border-radius: 12px;
  object-fit: contain;
}
.workflow-list {
  display: grid;
  gap: 8px;
  min-width: 0;
  padding: 16px;
  border: 1px solid #dce2ea;
  border-radius: 12px;
  background: #fff;
  list-style: none;
}
.workflow-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 64px;
  padding: 8px 16px;
  border: 1px solid #dce2ea;
  border-radius: 8px;
  font-size: 18px;
  line-height: 26px;
}
.step-number {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: #ed000c;
  background: #fff0f2;
  font-size: 14px;
}
@media (min-width: 768px) {
  .hero-copy h1 {
    font-size: 36px;
  }
  .hero-description {
    font-size: 18px;
  }
  .section-heading h2 {
    font-size: 26px;
  }
}
@media (min-width: 992px) {
  .hero-copy h1 {
    font-size: 40px;
  }
  .section-heading h2 {
    font-size: 28px;
  }
}
@media (min-width: 1200px) {
  .hero-copy h1 {
    font-size: 44px;
  }
  .section-heading h2 {
    font-size: 30px;
  }
}
@media (min-width: 1400px) {
  .section-heading h2 {
    font-size: 32px;
  }
}
@media (max-width: 991px) {
  .hero-layout {
    gap: 24px;
  }
  .workflow-layout {
    grid-template-columns: 1fr;
  }
  .workflow-image {
    max-height: 360px;
  }
}
@media (max-width: 767px) {
  .memcore-container {
    width: calc(100% - 32px);
  }
  .memcore-hero,
  .memcore-section {
    padding: 40px 0;
  }
  .hero-layout,
  .capability-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .hero-art {
    width: min(100%, 520px);
    margin: 8px auto 0;
  }
  .section-heading {
    margin-bottom: 24px;
  }
  .workflow-list li {
    padding: 12px;
    font-size: 16px;
  }
}
</style>
